/**
 * 學術風格設計系統 — 共用元件（紙感頁殼 / 紙片卡 / 學術按鈕）。
 *
 * 所有學習畫面一律用這套元件,不准再各畫各的（boss 2026-06-08;見 wiki
 * she-ji-xi-tong）。token single source = `src/learn/cover.ts`,基礎 class / 變數
 * 在 `./theme.ts`。每個元件 render 前都會 `ensureAcademicTheme()`,呼叫端不必自己注入。
 *
 * 設計鐵則:教科書 / 講義紙感、serif 標題、墨水文字、單一學術綠重點色、克制;
 * 無音效 / mascot / 彩光。
 */
import { ensureAcademicTheme } from './theme';
import { pushBackHandler, goBack } from '../nav/back-nav';
import { prefersReducedMotion } from '../nav/motion-pref';

/** `pageShell` 回傳結構。 */
export interface PageShell {
  /** 頁殼根節點（`.ef-page`,已 append 進 container)。 */
  root: HTMLElement;
  /** 內容區（`.ef-page__inner`)—— 把畫面內容塞進這裡。 */
  body: HTMLElement;
  /**
   * 取消註冊本頁的返回處理器(若有 `onBack`)。換頁時呼一次,把本頁從返回堆疊移除,
   * 維持「堆疊深度 = 可見畫面數」不變式。沒 `onBack` 時為 no-op。多數呼叫端走
   * main.ts `setMode`(會 `resetBackStack`),不必手動呼;door 內部子頁切換則該呼。
   */
  unmount: () => void;
}

/** `pageShell` 設定。 */
export interface PageShellOptions {
  /** 標題列主標(serif)。 */
  title: string;
  /** 選填副標(灰階說明文,標題下方)。 */
  subtitle?: string;
  /**
   * 選填返回行為。給定時:
   *   1. 在頁面左上畫統一的「‹ 返回」鈕(學術風),點擊 = `goBack()`。
   *   2. `pushBackHandler(onBack, { confirm: confirmOnBack })` 註冊進返回堆疊,讓
   *      左緣滑動返回 / ‹ 鈕都打到同一處理器。
   * 回傳的 `unmount()` 會取消註冊。
   */
  onBack?: () => void;
  /**
   * 選填確認字串(破壞性畫面用,如答題 / 考卷未存進度)。有值 → 不論 ‹ 鈕、左緣滑動
   * 還是程式呼 `goBack()`,都會先跳此確認字串,取消則不返回(不可靜默丟失作答)。
   */
  confirmOnBack?: string;
}

/**
 * 紙底頁殼 —— 學術風格畫面的統一外框（頁底 + 左上 ‹ 返回 + serif 標題列 + 內容區）。
 *
 * 自動 `ensureAcademicTheme()`、畫標題列。若給 `onBack`:
 *   - 在 `.ef-page__inner` 最上方畫統一「‹ 返回」鈕(`.ef-back-btn`),點擊 → `goBack()`。
 *   - 把 `onBack`(帶 `confirmOnBack`)推進集中式返回堆疊(`back-nav`),讓左緣滑動返回
 *     共用同一處理器。
 *   - 回傳 `unmount()` 取消註冊。
 *
 * 翻頁轉場(push):新頁從 `translateX(100%)` 滑入(`.ef-page--enter`);尊重
 * 「減少晃動」(`prefersReducedMotion()` 真 → 不加 class,瞬間出現)。返回的滑出由
 * `slideOutAndRemove()` 處理(door 子頁切換 / setMode 前可呼)。
 *
 * @param container 掛載點（main.ts 通常傳 learnRoot）。
 * @param opts      標題 / 副標 / 返回設定 / 破壞性確認字串。
 * @returns `{ root, body, unmount }` —— `root` 已 append 進 container;內容塞 `body`。
 */
export function pageShell(container: HTMLElement, opts: PageShellOptions): PageShell {
  ensureAcademicTheme();

  const root = document.createElement('div');
  root.className = 'ef-page';

  const inner = document.createElement('div');
  inner.className = 'ef-page__inner';
  root.appendChild(inner);

  // ── 統一 ‹ 返回 鈕(左上;onBack 才畫)──────────────────────────────
  let offBack: (() => void) | null = null;
  if (opts.onBack) {
    const onBack = opts.onBack;
    // 註冊進集中式返回堆疊(左緣滑動返回共用此處理器;confirmOnBack 在這裡生效)。
    offBack = pushBackHandler(onBack, { confirm: opts.confirmOnBack });

    const backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.className = 'ef-back-btn';
    backBtn.setAttribute('data-testid', 'page-back');
    backBtn.setAttribute('aria-label', '返回');
    backBtn.innerHTML =
      '<span class="ef-back-btn__chevron" aria-hidden="true">‹</span><span>返回</span>';
    // 點 ‹ = goBack():走堆疊頂端(= 本頁)處理器,confirmOnBack 由 back-nav 統一處理。
    backBtn.addEventListener('click', () => { goBack(); });
    inner.appendChild(backBtn);
  }

  const header = document.createElement('header');
  header.className = 'ef-page__header';

  const heading = document.createElement('div');
  heading.className = 'ef-page__heading';

  const h1 = document.createElement('h1');
  h1.className = 'ef-title';
  h1.textContent = opts.title;
  heading.appendChild(h1);

  if (opts.subtitle) {
    const sub = document.createElement('p');
    sub.className = 'ef-subtitle';
    sub.textContent = opts.subtitle;
    heading.appendChild(sub);
  }

  header.appendChild(heading);
  inner.appendChild(header);

  const body = document.createElement('div');
  body.className = 'ef-page__body';
  inner.appendChild(body);

  container.appendChild(root);

  // ── 翻頁轉場(push):新頁從右滑入。reduceMotion 開 → 不加 class(瞬切)。 ──
  if (!prefersReducedMotion()) {
    root.classList.add('ef-page--enter');
    root.addEventListener('animationend', () => root.classList.remove('ef-page--enter'), {
      once: true,
    });
  }

  const unmount = (): void => {
    if (offBack) { offBack(); offBack = null; }
  };

  return { root, body, unmount };
}

/**
 * 返回轉場(pop):把當前頁 `root` 往右滑出後移除,然後呼 `done()`(通常 = 換上一頁)。
 *
 * 尊重「減少晃動」:`prefersReducedMotion()` 真 → 直接移除 + `done()`(瞬切,不動畫)。
 * 給 door 子頁返回 / 任何「先滑出再換頁」的呼叫端用;不傳 root 或 root 已脫離 DOM 時
 * 仍會安全呼 `done()`。
 *
 * @param root 要滑出的頁殼根節點(pageShell 回傳的 `root`)。
 * @param done 滑出完成後的回呼(換頁)。
 */
export function slideOutAndRemove(root: HTMLElement | null, done: () => void): void {
  if (!root || !root.isConnected || prefersReducedMotion()) {
    root?.remove();
    done();
    return;
  }
  root.classList.add('ef-page--leave');
  let finished = false;
  const finish = (): void => {
    if (finished) return;
    finished = true;
    root.remove();
    done();
  };
  root.addEventListener('animationend', finish, { once: true });
  // 動畫事件沒觸發的保險(jsdom / 背景分頁):略長於 260ms 後強制收尾。
  setTimeout(finish, 320);
}

/** `academicCard` 設定。 */
export interface AcademicCardOptions {
  /** 選填小 icon（emoji 當點綴,不是主視覺)。 */
  icon?: string;
  /** 卡片標題（serif)。 */
  label: string;
  /** 選填說明文（灰階)。 */
  sub?: string;
  /** 點擊行為。 */
  onClick: () => void;
}

/**
 * 紙片卡 —— 學術風格的可點擊卡片（章節 / 功能入口)。
 *
 * 鍵盤可用:`role="button"` + `tabindex=0`，Enter / Space 觸發 `onClick`。
 *
 * @returns 卡片 `HTMLElement`(呼叫端自行 append)。
 */
export function academicCard(opts: AcademicCardOptions): HTMLElement {
  ensureAcademicTheme();

  const card = document.createElement('div');
  card.className = 'ef-card';
  card.setAttribute('role', 'button');
  card.setAttribute('tabindex', '0');

  if (opts.icon) {
    const icon = document.createElement('div');
    icon.className = 'ef-card__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = opts.icon;
    card.appendChild(icon);
  }

  const label = document.createElement('div');
  label.className = 'ef-card__label';
  label.textContent = opts.label;
  card.appendChild(label);

  if (opts.sub) {
    const sub = document.createElement('div');
    sub.className = 'ef-card__sub';
    sub.textContent = opts.sub;
    card.appendChild(sub);
  }

  card.setAttribute('aria-label', opts.label + (opts.sub ? `:${opts.sub}` : ''));
  card.addEventListener('click', opts.onClick);
  card.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      opts.onClick();
    }
  });

  return card;
}

/** 按鈕變體 —— 主 / 次 / 危險。 */
export type AcademicButtonVariant = 'primary' | 'secondary' | 'danger';

/** `academicButton` 設定。 */
export interface AcademicButtonOptions {
  /** 按鈕文字。 */
  label: string;
  /** 變體（預設 `primary`)。 */
  variant?: AcademicButtonVariant;
  /** 點擊行為。 */
  onClick: () => void;
}

/**
 * 學術按鈕 —— 主（學術綠實心) / 次（描邊) / 危險（紅描邊)三種變體。
 *
 * @returns `HTMLButtonElement`(`type="button"`，呼叫端自行 append)。
 */
export function academicButton(opts: AcademicButtonOptions): HTMLButtonElement {
  ensureAcademicTheme();

  const variant: AcademicButtonVariant = opts.variant ?? 'primary';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `ef-btn ef-btn--${variant}`;
  btn.textContent = opts.label;
  btn.addEventListener('click', opts.onClick);
  return btn;
}
